import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiLoading, mdiSend } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { apiBaseUrl } from '../../api/client'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { rememberServiceHolder, searchContacts } from '../../hooks/useScenariosApi'

/** A recipient a scenario step's resolver found (spec 2026-10-28-15 § 4). */
type ScenarioCandidate = { contact_uid?: string; email: string; name?: string; reason: string; preselected?: boolean }
type ScenarioRecipients = { candidates: ScenarioCandidate[]; missing?: string; missing_fix?: string }

/** A known provider a request for quotes may go to (spec 2026-10-27-22 §6). */
type SuggestedRecipient = { email: string; name: string; contract_name: string; site_name: string }

/** Actions that send one e-mail per recipient: no provider sees the others. */
const SEPARATE_SENDS_ACTIONS = new Set(['request_quotes'])

const splitRecipients = (value: string) => value.split(',').map((s) => s.trim()).filter(Boolean)

function authHeaders(): HeadersInit {
    const token = localStorage.getItem('token')
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

type Props = {
    orgCode: string
    issueUid: string
    issueTitle: string
    /** The catalogue default_action being resolved (order_diagnosis, …). */
    action: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** A chase prepared by the follow-up sweep (spec 2026-10-27-19 § 4). */
    initialSubject?: string
    initialBody?: string
    initialTo?: string
}

/**
 * Draft-and-confirm compose dialog for a send_email remediation action (spec
 * 2026-07-18-01 action engine). Opens pre-filled — recipient resolved server
 * side, subject/body from localized templates — for the manager to review
 * before the email leaves. On send, the ticket moves to "in progress".
 */
export function ActionComposeDialog({ orgCode, issueUid, issueTitle, action, open, onOpenChange, initialSubject, initialBody, initialTo }: Props) {
    const { t } = useTranslation('issues')
    const queryClient = useQueryClient()

    const [to, setTo] = useState('')
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [loadingPrep, setLoadingPrep] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // "Relancer si pas de réponse sous N jours": prefilled from
    // TICKET_FOLLOW_UP_DEFAULT (or the delay a previous send stored).
    const [followUpDays, setFollowUpDays] = useState('')
    const [suggested, setSuggested] = useState<SuggestedRecipient[]>([])
    // A scenario step: the resolved candidates, never a typed address.
    const [scenario, setScenario] = useState<ScenarioRecipients | null>(null)
    const [contactQuery, setContactQuery] = useState('')
    const [contactHits, setContactHits] = useState<{ uid: string; name?: string; email?: string }[]>([])
    const [picked, setPicked] = useState<{ uid: string; email: string; name?: string }[]>([])
    const [remember, setRemember] = useState(false)

    // On open, seed the draft: localized subject/body template + server-resolved
    // recipient. Reset everything when the dialog closes.
    useEffect(() => {
        if (!open) return
        setError(null)
        setSubject(initialSubject || t(`remediation.email.${action}.subject`, { defaultValue: t('remediation.compose.subject_fallback'), ticket: issueTitle }))
        setBody(initialBody || t(`remediation.email.${action}.body`, { defaultValue: t('remediation.compose.body_fallback'), ticket: issueTitle }))
        if (initialTo) setTo(initialTo)
        setFollowUpDays('')
        setSuggested([])
        setScenario(null)
        setPicked([])
        setContactHits([])
        setContactQuery('')
        setRemember(false)
        setLoadingPrep(true)
        fetch(`${apiBaseUrl}/api/v1/organizations/${orgCode}/issues/${issueUid}/action/prepare`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ action }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((prep) => {
                if (prep?.to && !initialTo) setTo(prep.to)
                if (prep?.follow_up_days) setFollowUpDays(String(prep.follow_up_days))
                // The catalogue draft, rendered server side from the ticket's facts.
                if (prep?.subject && !initialSubject) setSubject(prep.subject)
                if (prep?.body && !initialBody) setBody(prep.body)
                if (Array.isArray(prep?.suggested_recipients)) setSuggested(prep.suggested_recipients)
                if (prep?.scenario) setScenario({ candidates: prep.scenario.candidates ?? [], missing: prep.scenario.missing, missing_fix: prep.scenario.missing_fix })
            })
            .catch(() => {})
            .finally(() => setLoadingPrep(false))
    }, [open, action, issueUid, orgCode, issueTitle, t, initialSubject, initialBody, initialTo])

    const recipients = splitRecipients(to)
    const isChecked = (email: string) => recipients.some((r) => r.toLowerCase() === email.toLowerCase())
    const toggleSuggested = (email: string, checked: boolean) => {
        const others = recipients.filter((r) => r.toLowerCase() !== email.toLowerCase())
        setTo((checked ? [...others, email] : others).join(', '))
    }

    const canSend = to.trim() !== '' && subject.trim() !== '' && body.trim() !== '' && !sending
    // The service a step found nobody for (« no_service_holder:leak_detection »).
    const missingService = scenario?.missing?.includes(':') ? scenario.missing.split(':')[1] : null

    useEffect(() => {
        if (!scenario || contactQuery.trim().length < 2) {
            setContactHits([])
            return
        }
        const handle = setTimeout(() => {
            searchContacts(orgCode, contactQuery.trim()).then(setContactHits).catch(() => setContactHits([]))
        }, 250)
        return () => clearTimeout(handle)
    }, [contactQuery, orgCode, scenario])

    const pickContact = (c: { uid: string; email?: string; name?: string }) => {
        if (!c.email) return
        setPicked((p) => (p.some((x) => x.uid === c.uid) ? p : [...p, { uid: c.uid, email: c.email!, name: c.name }]))
        toggleSuggested(c.email, true)
        setContactQuery('')
        setContactHits([])
    }

    const send = async () => {
        setSending(true)
        setError(null)
        try {
            const res = await fetch(`${apiBaseUrl}/api/v1/organizations/${orgCode}/issues/${issueUid}/action`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    action,
                    email: {
                        to: recipients,
                        subject,
                        body,
                        follow_up_days: Number(followUpDays) > 0 ? Number(followUpDays) : undefined,
                    },
                }),
            })
            if (!res.ok) {
                const err = await res.json().catch(() => ({}))
                if (err?.code === 'RECIPIENT_NOT_RESOLVED') throw new Error('RECIPIENT_NOT_RESOLVED')
                throw new Error(`HTTP ${res.status}`)
            }
            // « Retenir ce contact pour ce service »: the contacts picked by hand
            // now hold the service the step found nobody for.
            if (remember && missingService) {
                await Promise.all(picked.map((c) => rememberServiceHolder(orgCode, c.uid, missingService).catch(() => undefined)))
            }
            queryClient.invalidateQueries()
            onOpenChange(false)
        } catch (e) {
            setError(e instanceof Error && e.message === 'RECIPIENT_NOT_RESOLVED' ? t('scenarios:errors.RECIPIENT_NOT_RESOLVED') : t('remediation.compose.error'))
        } finally {
            setSending(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="action-compose-dialog">
                <DialogHeader>
                    <DialogTitle>{t('remediation.compose.title')}</DialogTitle>
                    <DialogDescription>{t('remediation.compose.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {suggested.length > 0 && (
                        <div className="space-y-1.5" data-testid="compose-suggested-recipients">
                            <p className="text-xs font-medium text-muted-foreground">{t('remediation.compose.suggested')}</p>
                            <ul className="space-y-1">
                                {suggested.map((r, index) => (
                                    <li key={r.email} className="flex items-start gap-2">
                                        <Checkbox
                                            id={`compose-suggested-${index}`}
                                            checked={isChecked(r.email)}
                                            onCheckedChange={(checked) => toggleSuggested(r.email, checked === true)}
                                            data-testid={`compose-suggested-${index}`}
                                            className="mt-0.5"
                                        />
                                        <label htmlFor={`compose-suggested-${index}`} className="min-w-0 text-sm leading-tight">
                                            <span className="font-medium">{r.name || r.email}</span>
                                            {r.name && <span className="text-muted-foreground"> · {r.email}</span>}
                                            <span className="block break-words text-xs text-muted-foreground">
                                                {[r.contract_name, r.site_name].filter(Boolean).join(' · ')}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {scenario && (
                        <div className="space-y-1.5" data-testid="compose-scenario-recipients">
                            <p className="text-xs font-medium text-muted-foreground">{t('scenarios:compose.candidates')}</p>
                            {scenario.candidates.length > 0 ? (
                                <ul className="space-y-1">
                                    {scenario.candidates.map((c, index) => (
                                        <li key={c.email} className="flex items-start gap-2">
                                            <Checkbox
                                                id={`scenario-candidate-${index}`}
                                                checked={isChecked(c.email)}
                                                onCheckedChange={(checked) => toggleSuggested(c.email, checked === true)}
                                                data-testid={`compose-scenario-candidate-${index}`}
                                                className="mt-0.5"
                                            />
                                            <label htmlFor={`scenario-candidate-${index}`} className="min-w-0 text-sm leading-tight">
                                                <span className="font-medium">{c.name || c.email}</span>
                                                {c.name && <span className="text-muted-foreground"> · {c.email}</span>}
                                                <span className="block text-xs text-muted-foreground">{t(`scenarios:reasons.${c.reason}`, { defaultValue: c.reason })}</span>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-warning" data-testid="compose-scenario-missing">
                                    {t('scenarios:compose.missing_title')} — {t(`scenarios:missing.${(scenario.missing ?? '').split(':')[0]}`, {
                                        defaultValue: scenario.missing ?? '',
                                        service: missingService ? t(`scenarios:services.${missingService}`, { defaultValue: missingService }) : '',
                                    })}
                                    {scenario.missing_fix && (
                                        <a className="ml-1 underline" href={`/${orgCode}${scenario.missing_fix}`}>{t('scenarios:preview.fix')}</a>
                                    )}
                                </p>
                            )}
                            <label className="text-xs font-medium text-muted-foreground">{t('scenarios:compose.contacts')}</label>
                            <Input
                                value={contactQuery}
                                onChange={(e) => setContactQuery(e.target.value)}
                                placeholder={t('scenarios:compose.search')}
                                data-testid="compose-contact-search"
                            />
                            {contactHits.length > 0 && (
                                <ul className="rounded border border-border">
                                    {contactHits.map((c) => (
                                        <li key={c.uid}>
                                            <button type="button" className="w-full px-2 py-1 text-left text-sm hover:bg-muted" onClick={() => pickContact(c)} data-testid={`compose-contact-${c.uid}`}>
                                                {c.name || c.email} <span className="text-muted-foreground">· {c.email}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {picked.length > 0 && missingService && (
                                <label className="flex items-center gap-2 text-xs">
                                    <Checkbox checked={remember} onCheckedChange={(c) => setRemember(c === true)} data-testid="compose-remember-service" />
                                    {t('scenarios:compose.remember')}
                                </label>
                            )}
                            <p className="text-xs text-muted-foreground" data-testid="compose-scenario-to">{recipients.join(', ')}</p>
                        </div>
                    )}
                    <div className={scenario ? 'hidden' : 'space-y-1.5'}>
                        <label className="text-xs font-medium text-muted-foreground">{t('remediation.compose.to')}</label>
                        <div className="relative">
                            <Input
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                placeholder={t('remediation.compose.to_placeholder')}
                                data-testid="compose-to"
                            />
                            {loadingPrep && (
                                <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin absolute right-2 top-2.5" />
                            )}
                        </div>
                        {SEPARATE_SENDS_ACTIONS.has(action) && (
                            <p className="text-xs text-muted-foreground" data-testid="compose-separate-sends">
                                {t('remediation.compose.separate_sends')}
                            </p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">{t('remediation.compose.subject')}</label>
                        <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">{t('remediation.compose.body')}</label>
                        <Textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className="min-h-[160px]"
                            data-testid="compose-body"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-muted-foreground flex-1" htmlFor="compose-follow-up-days">
                            {t('follow_up.compose_days')}
                        </label>
                        <Input
                            id="compose-follow-up-days"
                            type="number"
                            min={0}
                            value={followUpDays}
                            onChange={(e) => setFollowUpDays(e.target.value)}
                            className="w-20"
                            data-testid="compose-follow-up-days"
                        />
                    </div>
                    {error && <p className="text-sm text-destructive" data-testid="compose-error">{error}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                        {t('remediation.compose.cancel')}
                    </Button>
                    <Button onClick={send} disabled={!canSend} data-testid="compose-send">
                        {sending ? <AppIcon path={mdiLoading} size="sm" className="mr-1 motion-safe:animate-spin" /> : <AppIcon path={mdiSend} size="sm" className="mr-1" />}
                        {t('remediation.compose.send')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
